import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getUser, getWallet } from "@/api/mockData";
import { t, formatRub, formatGNK, type Lang } from "@/lib/i18n";

interface ProfileProps {
  lang: Lang;
  onLangChange: (lang: Lang) => void;
  onNavigateToReferrals: () => void;
}

export function Profile({ lang, onLangChange, onNavigateToReferrals }: ProfileProps) {
  const user = getUser();
  const wallet = getWallet();
  const [notifications, setNotifications] = useState(user.notificationsEnabled);
  const [tab, setTab] = useState<"settings" | "history" | "security">("settings");

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="pt-3">
        <h1 className="font-display text-3xl font-black tracking-tight">{t("profile.title", lang)}</h1>
      </div>

      {/* User Card */}
      <div className="glass-card-featured rounded-2xl p-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/15 to-primary/8 flex items-center justify-center text-lg font-black font-display text-primary border border-primary/10">
          {user.avatar}
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-lg">{user.name}</h2>
          <p className="text-[13px] text-muted-foreground">{user.email}</p>
          <p className="text-[10px] text-muted-foreground/60 mt-1">
            {t("profile.memberSince", lang)}: {user.memberSince}
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="glass-card rounded-2xl p-4 text-center border border-primary/6">
          <p className="text-[8px] uppercase tracking-widest text-muted-foreground/70 font-medium">{t("profile.totalInvested", lang)}</p>
          <p className="font-mono-num text-base font-bold text-primary mt-1.5">{formatRub(user.totalInvested)}</p>
        </div>
        <div className="glass-card rounded-2xl p-4 text-center border border-chart-2/6">
          <p className="text-[8px] uppercase tracking-widest text-muted-foreground/70 font-medium">{t("dash.totalMined", lang)}</p>
          <p className="font-mono-num text-base font-bold text-chart-2 mt-1.5">{formatGNK(user.totalMined)} GNK</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-secondary/30 rounded-xl p-1 border border-white/[0.03]">
        {(["settings", "security", "history"] as const).map((key) => {
          const labels = {
            settings: t("profile.settings", lang),
            security: t("profile.security", lang),
            history: t("profile.history", lang),
          };
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 py-2.5 text-[11px] font-semibold rounded-lg transition-all duration-300 ${
                tab === key ? "bg-card text-foreground shadow-md shadow-black/20 border border-white/[0.04]" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {labels[key]}
            </button>
          );
        })}
      </div>

      {/* Settings Tab */}
      {tab === "settings" && (
        <Card className="glass-card border-0 rounded-2xl animate-fade-in">
          <CardContent className="p-4 space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">{t("profile.name", lang)}</Label>
              <Input
                defaultValue={user.name}
                className="h-10 bg-secondary/50 border-border/50 focus:border-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">{t("profile.email", lang)}</Label>
              <Input
                defaultValue={user.email}
                type="email"
                className="h-10 bg-secondary/50 border-border/50 focus:border-primary"
              />
            </div>

            {/* Language Toggle */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">{t("profile.language", lang)}</Label>
              <div className="flex gap-2">
                <button
                  onClick={() => onLangChange("ru")}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    lang === "ru"
                      ? "bg-primary/15 text-primary border border-primary/30"
                      : "bg-secondary text-muted-foreground border border-transparent hover:bg-secondary/80"
                  }`}
                >
                  🇷🇺 Русский
                </button>
                <button
                  onClick={() => onLangChange("en")}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    lang === "en"
                      ? "bg-primary/15 text-primary border border-primary/30"
                      : "bg-secondary text-muted-foreground border border-transparent hover:bg-secondary/80"
                  }`}
                >
                  🇬🇧 English
                </button>
              </div>
            </div>

            {/* Notifications Toggle */}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm">{t("profile.notifications", lang)}</span>
              <button
                onClick={() => setNotifications(!notifications)}
                className={`w-12 h-6 rounded-full transition-all duration-300 relative ${
                  notifications ? "bg-primary" : "bg-secondary"
                }`}
                role="switch"
                aria-checked={notifications}
              >
                <span
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${
                    notifications ? "left-6" : "left-0.5"
                  }`}
                />
              </button>
            </div>

            <button className="w-full h-12 rounded-2xl cta-primary font-display text-sm">
              {t("profile.save", lang)}
            </button>
          </CardContent>
        </Card>
      )}

      {/* Security Tab */}
      {tab === "security" && (
        <Card className="glass-card border-0 rounded-2xl animate-fade-in">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium">{t("profile.twoFactor", lang)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {t("profile.enabled", lang)}
                </p>
              </div>
              <span className="flex items-center gap-1.5 text-xs text-chart-3">
                <span className="w-2 h-2 rounded-full bg-chart-3 animate-pulse-slow" />
                {t("profile.enabled", lang)}
              </span>
            </div>
            <div className="border-t border-border/30 pt-4 space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                {lang === "ru" ? "Пароль" : "Password"}
              </Label>
              <Input
                type="password"
                defaultValue="••••••••••"
                className="h-10 bg-secondary/50 border-border/50 focus:border-primary"
                disabled
              />
              <Button variant="outline" className="w-full mt-2 h-9 text-sm border-border/50">
                {lang === "ru" ? "Сменить пароль" : "Change Password"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Tab */}
      {tab === "history" && (
        <Card className="glass-card border-0 rounded-2xl overflow-hidden animate-fade-in">
          <CardContent className="p-0">
            {wallet.transactions.slice(0, 8).map((tx, i) => (
              <div
                key={tx.id}
                className={`flex items-center justify-between px-4 py-3 ${
                  i !== 7 ? "border-b border-white/[0.03]" : ""
                }`}
              >
                <div>
                  <p className="text-sm truncate max-w-[200px]">{tx.description}</p>
                  <p className="text-[10px] text-muted-foreground">{tx.date}</p>
                </div>
                <span
                  className={`font-mono-num text-sm font-semibold ${
                    tx.amount > 0 ? "text-chart-3" : "text-destructive"
                  }`}
                >
                  {tx.amount > 0 ? "+" : ""}
                  {tx.currency === "RUB" ? formatRub(tx.amount) : `${formatGNK(tx.amount)} GNK`}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Referrals Link */}
      <button
        onClick={onNavigateToReferrals}
        className="w-full glass-card rounded-2xl p-4 flex items-center justify-between group hover:border-primary/15 transition-all duration-300"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary border border-primary/10">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="text-left">
            <p className="text-[13px] font-bold">{t("nav.referrals", lang)}</p>
            <p className="text-[10px] text-muted-foreground/70 mt-0.5">
              {lang === "ru" ? "Приглашайте друзей и получайте бонусы" : "Invite friends and earn bonuses"}
            </p>
          </div>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground group-hover:text-foreground transition-colors group-hover:translate-x-0.5 transition-transform">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      {/* Logout */}
      <button className="w-full h-11 rounded-2xl text-sm font-semibold border border-destructive/20 text-destructive/80 hover:bg-destructive/8 hover:text-destructive transition-all duration-300">
        {t("profile.logout", lang)}
      </button>
    </div>
  );
}
